import { ITableColumn } from '@busybusy/webapp-react-ui';
import useIntegratedEquipmentEnabled from 'components/domain/equipment/IntegratedEquipmentLocationDialog/hooks/useIntegratedEquipmentEnabled';
import useEquipmentInspectionsEnabled, {
  useEquipmentInspectionsGroupActionsEnabled,
} from 'hooks/feature/equipment/useEquipmentInspectionsEnabled/useEquipmentInspectionsEnabled';
import { t } from 'utils/localize';
import { getCreatedOnTableCell, getUpdatedOnTableCell } from 'utils/uiUtils';
import { IManageEquipmentRowInfo } from './ManageEquipment';
import { DateTime } from 'luxon';
import { localizeDateTimeFromUtcISO } from 'utils/dateUtils';

export const columns = [
  {
    key: 'name',
    sort: true,
    title: t('Name'),
    visibleByDefault: true,
  },
  {
    key: 'make',
    sort: true,
    title: t('Make'),
    visibleByDefault: true,
  },
  {
    key: 'model',
    sort: true,
    title: t('Model'),
    visibleByDefault: true,
  },
  {
    key: 'year',
    sort: true,
    title: t('Year'),
    visibleByDefault: true,
  },
  {
    key: 'category',
    sort: true,
    title: t('Category'),
    visibleByDefault: true,
  },
  {
    key: 'costHistory',
    sort: true,
    title: t('Hourly Rate'),
    visibleByDefault: true,
  },
  {
    key: 'hourMeter',
    sort: true,
    title: t('Hour Meter'),
    visibleByDefault: true,
  },
  {
    key: 'type',
    sort: true,
    title: t('Type'),
    visibleByDefault: false,
  },
  {
    key: 'createdOn',
    title: t('Created On'),
    sort: true,
    visibleByDefault: true,
    cell: (row: IManageEquipmentRowInfo) => (
      <>{localizeDateTimeFromUtcISO(row.createdOn).toFormat('LLL d, yyyy @ h:mm a')}</>
    ),
  },
  {
    key: 'updatedOn',
    title: t('Last Edited'),
    sort: true,
    visibleByDefault: true,
    cell: (row: IManageEquipmentRowInfo) => (
      <>{row.updatedOn ? localizeDateTimeFromUtcISO(row.updatedOn).toFormat('LLL d, yyyy @ h:mm a') : '---'}</>
    ),
  },
];

export function useDefaultVisibleColumns(): ITableColumn<IManageEquipmentRowInfo>[] {
  const equipmentInspectionsGroupActionsEnabled = useEquipmentInspectionsGroupActionsEnabled();
  const equipmentInspectionsEnabled = useEquipmentInspectionsEnabled();
  const hasGpsHardware = useIntegratedEquipmentEnabled();

  const defaultVisibleColumns = [...columns];

  if (hasGpsHardware) {
    defaultVisibleColumns.push({
      key: 'deviceEsn',
      sort: false,
      title: t('Device ESN'),
      visibleByDefault: true,
      cell: (row: IManageEquipmentRowInfo) => <>{row.deviceEsn ?? ''}</>,
    });
  }

  if (equipmentInspectionsGroupActionsEnabled) {
    defaultVisibleColumns.push({
      key: 'numEquipmentInspections',
      sort: false,
      title: t('Inspections'),
      visibleByDefault: true,
    });
  }

  if (equipmentInspectionsEnabled) {
    defaultVisibleColumns.push({
      key: 'lastInspected',
      sort: true,
      title: t('Last Inspected'),
      visibleByDefault: true,
      cell: (row: IManageEquipmentRowInfo) => (
        <>
          {row.lastInspected ? localizeDateTimeFromUtcISO(row.lastInspected).toFormat('LLL d, yyyy @ h:mm a') : '---'}
        </>
      ),
    });
  }

  return defaultVisibleColumns.map((column: ITableColumn<IManageEquipmentRowInfo>, index) => {
    return {
      key: column.key,
      visible: column.visibleByDefault,
      title: column.title,
      position: index,
      sort: column.sort,
      cell: column.cell,
    };
  });
}
